import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTenant } from "../../../Contexts/TenantContext";
import { accountService } from "../../../Api/Services/accountService";
import { toPublicPath } from "../../../Utils/publicRoutes";
import { isValidPassword, passwordRequirementsMessage } from "../../../Utils/passwordValidator";
import { toast } from "react-toastify";
import FloatingInput from "../../../Components/FloatingInput/FloatingInput";
import FooterPolicies from "../../../Components/FooterPolicies/FooterPolicies";
import Button from "../../../Components/Button/Button";

const UserResetPasswordPage = () => {
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const { tenant } = useTenant();

  const [newPassword, setNewPassword] = useState("");
  const [confirmNewpassword, setConfirmNewPassword] = useState("");
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const passwordTooltip = passwordRequirementsMessage.trim();

  const emailParam = searchParams.get('email') || "";
  const tokenParam = searchParams.get('token') || "";

  useEffect(() => {
    const isFilled = newPassword.trim().length > 0 && confirmNewpassword.trim().length > 0;
    const isMatch = newPassword === confirmNewpassword;
    setIsButtonEnabled(isFilled && isMatch);
  }, [newPassword, confirmNewpassword]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const resetFields = () => {
    setNewPassword("");
    setConfirmNewPassword("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValidPassword(newPassword)) {
      setErrorMessage(passwordTooltip);
      return;
    }

    try {
      const reset = await accountService.resetPassword({
        email: emailParam,
        token: tokenParam,
        newPassword: newPassword
      });

      if (!reset) {
        toast.error("O link de recuperação de senha expirou. Solicite uma nova recuperação.");
        resetFields();
        return;
      }

      toast.success("Senha alterada com sucesso!");
      navigate(toPublicPath("/login"));
    } catch (error) {
      toast.error("Não foi possível alterar sua senha, tente novamente mais tarde!");
      resetFields();
    }
  };

  return (
    <div className={`flex flex-col min-h-screen bg-white`}>
      <div className="flex-1 flex flex-col items-center px-4 py-10">
        <Link to="/" className="mb-8">
          <img
            src={tenant?.logo?.url}
            alt="Logo"
            className={`mx-auto mb-8 w-32`}
          />
        </Link>

        <div className={`w-full max-w-2xl bg-white`}>
          <h2 className={`font-semibold text-center mb-3 md:mb-8 text-xl md:text-2xl text-primary-600`}>
            {"RECUPERAR SENHA"}
          </h2>

          <p className={`text-center mb-3 md:mb-6 text-gray-600`}>
            {"Preencha os campos abaixo para criar uma nova senha."}
          </p>

          <form className="space-y-3 md:space-y-6">
            <FloatingInput
              label={"E-mail"}
              type="text"
              value={emailParam}
              onChange={() => {}}
              readOnly
              disabled
            />

            <FloatingInput
              label={"Digite a nova senha"}
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              tooltip={passwordTooltip}
            />

            <FloatingInput
              label={"Confirme a nova senha"}
              tooltip={passwordTooltip}
              type="password"
              value={confirmNewpassword}
              onChange={e => setConfirmNewPassword(e.target.value)}
            />

            {errorMessage && (
              <p className="text-primary-600 text-xs sm:text-sm">{errorMessage}</p>
            )}

            <Button
              onClick={handleSubmit}
              disabled={!isButtonEnabled}
              className="w-full py-3"
            >
              {"CONFIRMAR"}
            </Button>
          </form>
        </div>
      </div>

      <FooterPolicies />
    </div>
  );
};

export default UserResetPasswordPage;
